import React from 'react';

import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {OverflowButton} from 'ui-library/menus/OverflowMenu';
import Section from 'ui-library/menus/Section';

import {DataFrame} from '../DataFrameTypes';

export const TOOLBAR_OVERFLOW_MENU_SENTINEL = 'OVERFLOW_MENU' as const;

export interface ToolbarActionRenderParams {
  /** IDs are optional, though should be specified when available for analytic event tracking purposes. */
  dataFrameId?: string;
  actionOrigin: ActionOrigin;
  selectedRowIds: Set<string>;
  tableMode: DataFrameTableMode;
  setTableMode: (newTableMode: DataFrameTableMode) => void;
  /**
   * True while resolving data for rows selected on other, non-visible pages of a server-paginated table
   * (see `useSelectedRowsAcrossPages`). Actions that filter `dataFrame.rows` by `selectedRowIds` should
   * disable themselves while this is true, since the selection isn't fully resolved on `dataFrame` yet.
   */
  isResolvingSelectedRows?: boolean;
  /**
   * True if resolving selected rows on other pages (see above) failed, meaning `dataFrame` may be missing
   * some selected rows' data. Actions that filter `dataFrame.rows` by `selectedRowIds` should disable
   * themselves while this is true, rather than silently acting on an incomplete selection.
   */
  hasErrorLoadingSelectedRows?: boolean;
}

// Required to return a rendered DataFrameActionButton
export type PrimaryActionRenderFn = (dataFrame: DataFrame, params: ToolbarActionRenderParams) => JSX.Element;

// Required to return a rendered <Option/>, optionally with a sub-menu
export type OverflowActionRenderFn = (dataFrame: DataFrame, params: ToolbarActionRenderParams) => JSX.Element;

export interface DataFrameActionToolbarProps {
  // The toolbar will render its action buttons, plus an optional overflow menu,
  // in the order specified by this prop.
  // TOOLBAR_OVERFLOW_MENU_SENTINEL should be included at most once.
  renderElements: Array<PrimaryActionRenderFn | typeof TOOLBAR_OVERFLOW_MENU_SENTINEL>;
  // Each sub-array represents a <Section/> of the <Menu/>
  overflowMenu?: Array<Array<OverflowActionRenderFn>>;
  dataFrame: DataFrame;
  params: ToolbarActionRenderParams;
}

export function validateDataFrameActionToolbarProps({
  renderElements,
  overflowMenu,
}: Pick<DataFrameActionToolbarProps, 'renderElements' | 'overflowMenu'>): void {
  if (overflowMenu) {
    assert(
      renderElements.filter((element) => element === TOOLBAR_OVERFLOW_MENU_SENTINEL).length === 1,
      '`TOOLBAR_OVERFLOW_MENU_SENTINEL` must be included in `renderElements` if `overflowMenu` prop is specified'
    );
    assert(
      overflowMenu.every((section) => section.length > 0),
      'Each section of the overflow menu must have at least 1 option'
    );
  } else {
    assert(
      !renderElements.includes(TOOLBAR_OVERFLOW_MENU_SENTINEL),
      '`overflowMenu` prop must be specified if `TOOLBAR_OVERFLOW_MENU_SENTINEL` is included in `renderElements`'
    );
  }
}

type RenderOverflowMenuProps = Pick<DataFrameActionToolbarProps, 'dataFrame' | 'params'> & {
  overflowMenu: Array<Array<OverflowActionRenderFn>>;
};

function renderOverflowMenu({overflowMenu, dataFrame, params}: RenderOverflowMenuProps): JSX.Element {
  const menu = (
    <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
      {overflowMenu.map((section, index) => (
        <Section key={index.toString()}>{section.map((option) => option(dataFrame, params))}</Section>
      ))}
    </Menu>
  );

  return (
    <Dropdown menu={menu} key="OverflowMenuDropdown">
      <OverflowButton className="u-mini-spacing-left" enableTooltip={true} />
    </Dropdown>
  );
}

export default function DataFrameActionToolbar({
  renderElements,
  overflowMenu,
  dataFrame,
  params,
}: DataFrameActionToolbarProps): JSX.Element {
  validateDataFrameActionToolbarProps({renderElements, overflowMenu});
  return (
    <>
      {renderElements.map((actionElement) =>
        actionElement === TOOLBAR_OVERFLOW_MENU_SENTINEL
          ? renderOverflowMenu({overflowMenu: overflowMenu!, dataFrame, params})
          : actionElement(dataFrame, params)
      )}
    </>
  );
}
